import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ActiveSession } from '@/hooks/useDashboardData';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowRight, Clock, ListTodo, Sparkles, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { 
  getSessionProgressInfo, 
  getPhaseFromSubphase 
} from '@/lib/utils/sessionProgress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ActiveSessionsListProps {
  sessions: ActiveSession[];
  showEmptyState?: boolean;
  maxVisible?: number;
  onNewDecision?: () => void;
}

export const ActiveSessionsList = ({ sessions, showEmptyState = true, maxVisible = 3, onNewDecision }: ActiveSessionsListProps) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const getPhaseLabel = (subphase: string) => {
    const phase = getPhaseFromSubphase(subphase);
    
    // Subphase 3.4 = Report/Abgeschlossen (logisch "Phase 4")
    if (subphase === '3.4') {
      return { label: 'Abgeschlossen', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20' };
    }
    
    switch (phase) {
      case 1:
        return { label: 'Phase 1', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20' };
      case 2:
        return { label: 'Phase 2', color: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20' };
      case 3:
        return { label: 'Phase 3', color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' };
      default:
        return { label: 'Phase 1', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20' };
    }
  };

  const getDecisionTitle = (session: ActiveSession) => {
    if (session.summary) return session.summary;
    if (session.decision_type && session.decision_subtype) {
      return `${session.decision_type} – ${session.decision_subtype}`;
    }
    return session.decision_type || 'Neue Entscheidung';
  };

  const formatLastUpdated = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { locale: de, addSuffix: true });
    } catch {
      return 'Kürzlich';
    }
  };

  const EmptyState = ({ message, description, showCTA = false }: { message: string; description: string; showCTA?: boolean }) => (
    <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6 transition-all duration-500 hover:border-primary/20">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
            <ListTodo className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Aktive Entscheidungen</h3>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>
        </div>
        <div className="border-t border-border/50 pt-6 space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {description}
          </p>
          {showCTA && onNewDecision && (
            <Button
              onClick={onNewDecision}
              className="w-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Neue Entscheidung starten
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  if (sessions.length === 0 && !showEmptyState) {
    return (
      <EmptyState 
        message="Keine Entscheidungen gefunden"
        description="Keine Entscheidungen entsprechen den ausgewählten Filtern"
      />
    );
  }

  if (sessions.length === 0) {
    return (
      <EmptyState 
        message="Du hast noch keine aktiven Entscheidungen"
        description="Starte jetzt deine erste strukturierte Entscheidung"
        showCTA
      />
    );
  }

  const visibleSessions = isExpanded ? sessions : sessions.slice(0, maxVisible);
  const hasMore = sessions.length > maxVisible;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-500 hover:border-primary/20">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
            <ListTodo className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-semibold text-foreground">Aktive Entscheidungen</h3>
              {/* Badge inline with title */}
              <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                <Sparkles className="w-2.5 h-2.5" />
                {sessions.length}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {sessions.length} {sessions.length === 1 ? 'Entscheidung' : 'Entscheidungen'} in Bearbeitung
            </p>
          </div>
        </div>

        {/* Sessions List */}
        <div className="space-y-3">
          {visibleSessions.map((session) => {
            const phaseInfo = getPhaseLabel(session.subphase);
            const progressInfo = getSessionProgressInfo(session.subphase);

            return (
              <div
                key={session.id}
                className="group relative border border-border/50 rounded-xl p-3 sm:p-4 transition-all duration-300 hover:border-primary/30 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_hsl(var(--primary)/0.15)] overflow-hidden"
              >
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" aria-hidden="true" />
                
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0 space-y-2 sm:space-y-3">
                    <div className="flex items-start sm:items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-sm sm:text-base text-foreground line-clamp-2 sm:truncate group-hover:text-primary transition-colors">
                        {getDecisionTitle(session)}
                      </h4>
                      <Badge className={`${phaseInfo.color} border text-[10px] sm:text-xs flex-shrink-0`}>
                        {phaseInfo.label}
                      </Badge>
                    </div>

                    {/* Progress Bar - integriert im Card-Design */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className="space-y-1 sm:space-y-1.5 cursor-help"
                            role="progressbar"
                            aria-valuenow={progressInfo.progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Entscheidungsfortschritt: ${progressInfo.progress}%, aktueller Schritt: ${progressInfo.label}`}
                          >
                            <div className="flex items-center justify-between text-[10px] sm:text-xs">
                              <span className="text-muted-foreground truncate">{progressInfo.label}</span>
                              <span className="font-medium text-foreground flex-shrink-0 ml-2" aria-hidden="true">{progressInfo.progress}%</span>
                            </div>
                            <div className="h-1 sm:h-1.5 w-full bg-muted/20 rounded-full overflow-hidden" aria-hidden="true">
                              <div 
                                className="h-full bg-primary/70 rounded-full transition-all duration-700 ease-out"
                                style={{ width: `${progressInfo.progress}%` }}
                              />
                            </div>
                            {/* Phase-Segmente: P1 | P2 | P3 | Report */}
                            <div className="hidden sm:flex items-center gap-0.5 mt-1" aria-hidden="true">
                              <div className={`h-0.5 flex-1 rounded-full transition-colors ${progressInfo.progress >= 10 ? 'bg-primary/60' : 'bg-muted/20'}`} />
                              <div className={`h-0.5 flex-1 rounded-full transition-colors ${progressInfo.progress >= 40 ? 'bg-primary/60' : 'bg-muted/20'}`} />
                              <div className={`h-0.5 flex-1 rounded-full transition-colors ${progressInfo.progress >= 55 ? 'bg-primary/60' : 'bg-muted/20'}`} />
                              <div className={`h-0.5 flex-1 rounded-full transition-colors ${progressInfo.progress >= 100 ? 'bg-primary/60' : 'bg-muted/20'}`} />
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          <p className="font-medium">Aktueller Schritt: {progressInfo.label}</p>
                          <p className="text-muted-foreground">Subphase {session.subphase}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4 text-[10px] sm:text-xs text-muted-foreground">
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <Clock className="h-3 w-3" />
                        <span className="truncate">{formatLastUpdated(session.updated_at)}</span>
                      </div>
                      {/* Mobile: Button inline */}
                      <Button
                        onClick={() => navigate(`/app/session/${session.id}`)}
                        variant="outline"
                        size="sm"
                        className="sm:hidden h-7 text-xs border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
                      >
                        Weiter
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>

                  {/* Desktop: Button on the right */}
                  <div className="hidden sm:flex items-center flex-shrink-0">
                    <Button
                      onClick={() => navigate(`/app/session/${session.id}`)}
                      variant="outline"
                      size="sm"
                      className="border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all group/btn"
                    >
                      Fortsetzen
                      <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover/btn:translate-x-0.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show more/less button */}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-4 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5"
          >
            {isExpanded ? (
              <>
                Weniger anzeigen
                <ChevronUp className="h-3.5 w-3.5 ml-1" />
              </>
            ) : (
              <>
                +{sessions.length - maxVisible} weitere Entscheidungen
                <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
